import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Star, Trash2, MessageSquare } from "lucide-react";

function Stars({ value, size = 16, onClick, testId }) {
    return (
        <div className={`inline-flex gap-0.5 ${onClick ? "cursor-pointer" : ""}`} data-testid={testId}>
            {[1, 2, 3, 4, 5].map((n) => (
                <Star
                    key={n}
                    onClick={onClick ? () => onClick(n) : undefined}
                    style={{ width: size, height: size }}
                    className={n <= Math.round(value) ? "fill-[#FFCC00] text-[#FFCC00]" : "text-gray-300"}
                />
            ))}
        </div>
    );
}

export function DealerRatingSummary({ summary, large }) {
    if (!summary || !summary.count) {
        return <div className="text-xs text-gray-500" data-testid="rating-summary-empty">Noch keine Bewertungen</div>;
    }
    return (
        <div className="flex items-center gap-2 flex-wrap" data-testid="rating-summary">
            <Stars value={summary.avg || 0} size={large ? 18 : 14} />
            <span className={`font-mono-num font-bold ${large ? "text-base" : "text-xs"} text-[#111827]`}>{summary.avg?.toFixed(1)}</span>
            <span className={`text-gray-500 ${large ? "text-sm" : "text-xs"}`}>({summary.count})</span>
            {summary.responds_fast && (
                <span
                    className="inline-flex items-center gap-1 bg-green-100 text-green-800 border border-green-300 px-1.5 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider"
                    title={`Reagiert im Schnitt in ${summary.response_avg_hours ?? "?"} h`}
                    data-testid="dealer-fast-responder-badge"
                >
                    <MessageSquare className="w-2.5 h-2.5" /> Antwortet schnell
                </span>
            )}
        </div>
    );
}

// Phase 3.33 — Verteilungs-Bar-Chart der Sterne
export function ReviewDistributionChart({ summary }) {
    if (!summary || !summary.count || !summary.distribution) return null;
    const total = summary.count;
    return (
        <div className="space-y-1.5 mt-3" data-testid="review-distribution-chart">
            {[5, 4, 3, 2, 1].map((n) => {
                const cnt = summary.distribution?.[String(n)] || 0;
                const pct = total ? Math.round((cnt / total) * 100) : 0;
                return (
                    <div key={n} className="flex items-center gap-2 text-xs" data-testid={`distribution-row-${n}`}>
                        <span className="w-6 font-mono-num font-bold text-[#111827]">{n}★</span>
                        <div className="flex-1 h-2 bg-gray-100 rounded-sm overflow-hidden">
                            <div
                                className="h-full bg-[#FFCC00] transition-all"
                                style={{ width: `${pct}%` }}
                            />
                        </div>
                        <span className="w-12 text-right font-mono-num text-gray-600 text-[11px]">{cnt} · {pct}%</span>
                    </div>
                );
            })}
        </div>
    );
}

export default function DealerReviews({ dealerId, dealerName }) {
    const { user } = useAuth();
    const [data, setData] = useState({ summary: null, items: [] });
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState({ rating: 5, title: "", comment: "" });
    const [busy, setBusy] = useState(false);
    // Phase 3.30 — Sortierung
    const [sort, setSort] = useState("newest");
    // Phase 3.32 — Sterne-Filter (null=alle, 1..5)
    const [ratingFilter, setRatingFilter] = useState(null);
    // Phase 3.30 — Antwort-Editor (review_id im Editor-Mode)
    const [replyFor, setReplyFor] = useState(null);
    const [replyText, setReplyText] = useState("");
    const [replyBusy, setReplyBusy] = useState(false);

    const load = async () => {
        try {
            const q = new URLSearchParams({ sort });
            if (ratingFilter) q.set("rating", String(ratingFilter));
            const { data } = await api.get(`/dealers/${dealerId}/reviews?${q.toString()}`);
            setData(data);
            const own = data.items.find((r) => r.reviewer_id === user?.id);
            if (own) setForm({ rating: own.rating, title: own.title || "", comment: own.comment || "" });
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-line */ }, [dealerId, user?.id, sort, ratingFilter]);

    // Auto-open form + scroll into view when arriving with #reviews anchor
    // (e.g. from the Buyer-Dashboard "Händler bewerten"-CTA).
    useEffect(() => {
        if (loading) return;
        if (typeof window === "undefined") return;
        if (window.location.hash !== "#reviews") return;
        if (user && user.role === "b2c" && user.id !== dealerId) {
            setShowForm(true);
        }
        const el = document.querySelector('[data-testid="reviews-block"]');
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [loading, user, dealerId]);

    const submit = async () => {
        if (!form.rating || form.rating < 1 || form.rating > 5) {
            toast.error("Bitte 1-5 Sterne wählen");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/dealers/${dealerId}/reviews`, {
                rating: form.rating,
                title: form.title || undefined,
                comment: form.comment || undefined,
            });
            toast.success("Bewertung gespeichert");
            setShowForm(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
        } finally {
            setBusy(false);
        }
    };

    const remove = async (rid) => {
        if (!window.confirm("Bewertung löschen?")) return;
        try {
            await api.delete(`/reviews/${rid}`);
            toast.success("Gelöscht");
            load();
        } catch {
            toast.error("Löschen fehlgeschlagen");
        }
    };

    // Phase 3.30 — Antwort senden / löschen
    const submitReply = async (rid) => {
        const txt = (replyText || "").trim();
        if (!txt) { toast.error("Antworttext fehlt"); return; }
        setReplyBusy(true);
        try {
            await api.post(`/reviews/${rid}/reply`, { text: txt });
            toast.success("Antwort gespeichert");
            setReplyFor(null);
            setReplyText("");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Antwort fehlgeschlagen");
        } finally {
            setReplyBusy(false);
        }
    };

    const deleteReply = async (rid) => {
        if (!window.confirm("Antwort löschen?")) return;
        try {
            await api.delete(`/reviews/${rid}/reply`);
            toast.success("Antwort entfernt");
            load();
        } catch {
            toast.error("Löschen fehlgeschlagen");
        }
    };

    const ownReview = data.items.find((r) => r.reviewer_id === user?.id);
    const canReview = user && user.role === "b2c" && user.id !== dealerId;
    const isDealerOwner = user && user.role === "b2b" && user.id === dealerId;

    if (loading) return <div className="text-sm text-gray-500">Lädt Bewertungen…</div>;

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-6 mt-6" data-testid="reviews-block">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                <div className="flex-1 min-w-[280px]">
                    <h3 className="font-display text-xl font-bold mb-1">Bewertungen</h3>
                    <DealerRatingSummary summary={data.summary} large />
                    {(data.summary?.count || 0) >= 1 && <ReviewDistributionChart summary={data.summary} />}
                    <p className="text-[11px] text-gray-500 mt-2" data-testid="reviews-authenticity-note">
                        Echtheit: Es werden ausschließlich Bewertungen <b>angemeldeter Privatkund:innen</b> veröffentlicht; pro Person ist nur eine Bewertung je Händler möglich.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {data.items.length > 0 && (
                        <select
                            value={sort}
                            onChange={(e) => setSort(e.target.value)}
                            className="rounded-sm border border-gray-300 px-2 py-1.5 text-xs font-bold uppercase tracking-wider bg-white hover:border-[#111827]"
                            data-testid="reviews-sort-select"
                            aria-label="Bewertungen sortieren"
                        >
                            <option value="newest">Neueste zuerst</option>
                            <option value="highest">Höchste Bewertung</option>
                            <option value="lowest">Niedrigste Bewertung</option>
                        </select>
                    )}
                    {canReview && !showForm && (
                        <Button
                            onClick={() => setShowForm(true)}
                            className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider"
                            data-testid="btn-write-review"
                        >
                            {ownReview ? "Bewertung bearbeiten" : "Händler bewerten"}
                        </Button>
                    )}
                </div>
            </div>

            {showForm && (
                <div className="border border-gray-200 rounded-sm p-4 bg-gray-50 mb-4" data-testid="review-form">
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Ihre Bewertung</Label>
                    <div className="mt-2 mb-3">
                        <Stars value={form.rating} size={28} onClick={(n) => setForm({ ...form, rating: n })} testId="review-stars-input" />
                    </div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Titel</Label>
                    <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="z. B. Top Beratung & faire Preise" className="rounded-sm mt-1 mb-3" data-testid="review-title-input" />
                    <Label className="text-xs font-bold uppercase tracking-wider text-gray-500">Kommentar</Label>
                    <Textarea value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} rows={4} placeholder={`Wie war Ihr Erlebnis bei ${dealerName}?`} className="rounded-sm mt-1 mb-3" data-testid="review-comment-input" />
                    <div className="flex gap-2">
                        <Button onClick={submit} disabled={busy} className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#111827] font-bold rounded-sm uppercase tracking-wider" data-testid="review-submit">
                            {busy ? "Sendet…" : ownReview ? "Aktualisieren" : "Bewertung absenden"}
                        </Button>
                        <Button onClick={() => setShowForm(false)} variant="outline" className="rounded-sm font-bold">Abbrechen</Button>
                    </div>
                </div>
            )}

            {/* Phase 3.32 — Sterne-Filter (Pills): nur sichtbar wenn mindestens 2 Reviews */}
            {(data.summary?.count || 0) >= 2 && (
                <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="review-rating-filter">
                    <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mr-1">Filter:</span>
                    <button
                        onClick={() => setRatingFilter(null)}
                        className={`px-2.5 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wider ${ratingFilter === null ? "bg-[#111827] text-white" : "bg-white border border-gray-300 text-gray-700 hover:border-[#111827]"}`}
                        data-testid="review-filter-all"
                    >
                        Alle ({data.summary?.count || 0})
                    </button>
                    {[5, 4, 3, 2, 1].map((n) => {
                        const cnt = data.summary?.distribution?.[String(n)] || 0;
                        return (
                            <button
                                key={n}
                                onClick={() => setRatingFilter(ratingFilter === n ? null : n)}
                                disabled={cnt === 0}
                                className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-sm text-[11px] font-bold uppercase tracking-wider ${ratingFilter === n ? "bg-[#FFCC00] text-[#111827]" : "bg-white border border-gray-300 text-gray-700 hover:border-[#111827]"} disabled:opacity-40 disabled:cursor-not-allowed`}
                                data-testid={`review-filter-${n}`}
                            >
                                {n}★ ({cnt})
                            </button>
                        );
                    })}
                </div>
            )}

            {data.items.length === 0 ? (
                <div className="text-sm text-gray-500 py-6 text-center">
                    {ratingFilter ? `Keine Bewertungen mit ${ratingFilter} Stern${ratingFilter === 1 ? "" : "en"}.` : `Noch keine Bewertungen für ${dealerName}.`}
                </div>
            ) : (
                <div className="space-y-4" data-testid="reviews-list">
                    {data.items.map((r) => (
                        <div key={r.id} className="border-t border-gray-100 pt-4" data-testid={`review-${r.id}`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Stars value={r.rating} size={14} />
                                        <span className="text-xs font-bold text-[#111827]">{r.reviewer_name || "Käufer"}</span>
                                        <span className="text-xs text-gray-400">· {new Date(r.created_at).toLocaleDateString("de-DE")}</span>
                                    </div>
                                    {r.title && <div className="font-bold text-sm mb-1">{r.title}</div>}
                                    {r.comment && <p className="text-sm text-gray-700">{r.comment}</p>}
                                </div>
                                {r.reviewer_id === user?.id && (
                                    <button onClick={() => remove(r.id)} className="text-gray-400 hover:text-[#DC2626]" data-testid={`review-remove-${r.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                )}
                            </div>

                            {/* Phase 3.30 — Antwort vom Händler */}
                            {r.reply_text && replyFor !== r.id && (
                                <div className="mt-3 ml-4 pl-3 border-l-2 border-[#FFCC00] bg-[#FFCC00]/10 rounded-r-sm py-2 px-3" data-testid={`review-reply-${r.id}`}>
                                    <div className="flex items-center gap-2 mb-1">
                                        <MessageSquare className="w-3 h-3 text-[#111827]" />
                                        <span className="text-[10px] font-bold uppercase tracking-wider text-[#111827]">
                                            Antwort von {dealerName || "Händler"}
                                        </span>
                                        {r.reply_at && (
                                            <span className="text-[10px] text-gray-500">· {new Date(r.reply_at).toLocaleDateString("de-DE")}</span>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-800">{r.reply_text}</p>
                                    {isDealerOwner && (
                                        <div className="mt-2 flex gap-2">
                                            <button
                                                onClick={() => { setReplyFor(r.id); setReplyText(r.reply_text || ""); }}
                                                className="text-[10px] font-bold uppercase tracking-wider text-[#111827] hover:underline"
                                                data-testid={`review-reply-edit-${r.id}`}
                                            >
                                                Bearbeiten
                                            </button>
                                            <button
                                                onClick={() => deleteReply(r.id)}
                                                className="text-[10px] font-bold uppercase tracking-wider text-red-700 hover:underline"
                                                data-testid={`review-reply-delete-${r.id}`}
                                            >
                                                Antwort löschen
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Phase 3.30 — Antwort-CTA für Händler ohne bestehende Antwort */}
                            {isDealerOwner && !r.reply_text && replyFor !== r.id && (
                                <button
                                    onClick={() => { setReplyFor(r.id); setReplyText(""); }}
                                    className="mt-3 ml-4 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-[#111827] hover:text-[#FFCC00]"
                                    data-testid={`review-reply-cta-${r.id}`}
                                >
                                    <MessageSquare className="w-3 h-3" /> Antworten
                                </button>
                            )}

                            {/* Phase 3.30 — Antwort-Editor */}
                            {isDealerOwner && replyFor === r.id && (
                                <div className="mt-3 ml-4 p-3 border border-[#FFCC00] rounded-sm bg-[#FFCC00]/5" data-testid={`review-reply-form-${r.id}`}>
                                    <Label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                        Ihre öffentliche Antwort (max. 2.000 Zeichen)
                                    </Label>
                                    <Textarea
                                        value={replyText}
                                        onChange={(e) => setReplyText(e.target.value)}
                                        rows={3}
                                        maxLength={2000}
                                        placeholder="Danke für Ihre Bewertung. Wir freuen uns…"
                                        className="rounded-sm mt-1 mb-2"
                                        data-testid={`review-reply-input-${r.id}`}
                                        autoFocus
                                    />
                                    <div className="flex gap-2">
                                        <Button
                                            onClick={() => submitReply(r.id)}
                                            disabled={replyBusy || !replyText.trim()}
                                            className="bg-[#111827] hover:bg-black text-white font-bold rounded-sm text-xs uppercase tracking-wider"
                                            data-testid={`review-reply-submit-${r.id}`}
                                            size="sm"
                                        >
                                            {replyBusy ? "Sendet…" : "Antwort veröffentlichen"}
                                        </Button>
                                        <Button
                                            onClick={() => { setReplyFor(null); setReplyText(""); }}
                                            variant="outline"
                                            size="sm"
                                            className="rounded-sm font-bold text-xs"
                                            data-testid={`review-reply-cancel-${r.id}`}
                                        >
                                            Abbrechen
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
